/**
 * Recurring Job status and running pills.
 *
 * Not `defineStatusTag`: `status` is a tenant-configured EntitySubStatus carrying its own colour,
 * so there is no closed enum to map to a StatusTone. Each pill is one function serving BOTH the
 * manifest's `presentation.tags` and the manage runtime's `tags`, so a status cannot render one
 * colour on the board card and another in the header.
 */
import { Entity, RecurringJob } from '@jms/model';
import { EntityManifestTagStyle, EntityManifestTagStyleResolver } from '@workbuddy/services/entities/entity.manifest';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { TagDeriverCapability } from '@workbuddy/ui/v2/surface.engine';

type RecurringJobTag = TagDeriverCapability<RecurringJob> & EntityManifestTagStyleResolver<RecurringJob>;

/** A tint carries the three manifest colours plus the label the surface header prints. */
type TintedTag = EntityManifestTagStyle & { label: string };

export const recurringJobStatusTag: RecurringJobTag = value => {

  const status = value as Entity | null | undefined;
  return tint(status?.name ?? 'Not set', status?.color);
};

export const recurringJobRunningTag: RecurringJobTag = value => tint(value === true ? 'Running' : 'Stopped', value === true ? runningTint : undefined);

// Literal hex, not `var(--wb-*)`: these feed `oklch(from …)` relative-colour math below, whose origin
// must be a resolved colour — and the tenant's own status colour arrives as hex the same way.
const neutralTint = DesignSystem.tokens.color.foreground.quaternary;
const runningTint = DesignSystem.tokens.color.foreground.successPrimary;

function tint(label: string, color?: string): TintedTag {

  const tintColor = !color || color === 'transparent' || color.toUpperCase() === '#FFFFFF' ? neutralTint : color;
  return {
    label,
    color: `oklch(from ${tintColor} 0.45 c h)`,
    backgroundColor: `color-mix(in srgb, ${tintColor} 14%, #fff)`,
    borderColor: `oklch(from ${tintColor} 0.85 c h)`,
  };
}
